// The v2 client's state in one line, where the athlete edits: offline with
// edits waiting, reconnecting, and every edit that didn't land (the edit
// itself has already gone back where it was). A failure reads in the
// coach's voice, never as the athlete's fault (failures.js), with Try again
// (the same command, sent afresh), Dismiss, and the backend's reason under
// Details for support.
import React, { useEffect, useSyncExternalStore } from 'react';
import { InlineNote } from '../ds';
import { useV2, useV2State } from '../../context/V2Context';
import FailureNote from './FailureNote';

// The coach thread (`primary`) shows a refused message under its own bubble
// (Not sent · Try again · Remove), so its list leaves those out.
// One on screen at a time (#69): the coach thread's (`primary`) wins while
// it is open; the calendar's shows only when the thread is closed. Two
// copies read every failure twice, and announced it twice.
let primaries = 0;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());
const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const primaryShown = () => primaries > 0;

export default function SyncStatus({ primary = false }) {
  const { client } = useV2();
  const { status, pending, failed } = useV2State();
  useEffect(() => {
    if (!primary) return undefined;
    primaries += 1; notify();
    return () => { primaries -= 1; notify(); };
  }, [primary]);
  const covered = useSyncExternalStore(subscribe, primaryShown, () => false);
  if (!client || (!primary && covered)) return null;
  const waiting = pending.filter((c) => c.status === 'queued' || c.status === 'sending').length;
  // Online, but an edit didn't get through (a server error): it is being tried
  // again on a timer, so say so rather than sit silent (aithlo-frontend#65).
  const retrying = pending.filter((c) => c.status === 'queued' && c.tries > 0).length;
  return (
    <div className="shrink-0 space-y-2" aria-live="polite">
      {(status === 'offline' || (status === 'reconnecting' && waiting > 0)) && (
        <InlineNote tone="warning"
          text={`${status === 'offline' ? 'Offline.' : 'Reconnecting…'}${waiting > 0 ? ` ${waiting} edit${waiting === 1 ? '' : 's'} will be sent when you're back.` : ''}`} />
      )}
      {status === 'live' && retrying > 0 && (
        <InlineNote tone="warning" text={`${waiting} edit${waiting === 1 ? '' : 's'} waiting: trying again shortly.`} />
      )}
      {failed.filter((f) => !(primary && f.op?.kind === 'message')).map((f) => (
        <FailureNote key={f.key} failure={f} onRetry={() => client.retry(f.key)} onDismiss={() => client.dismissFailure(f.key)} />
      ))}
    </div>
  );
}
